import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface FormData { name: string; email: string; subject: string; message: string; }
interface FormErrors { name?: string; email?: string; subject?: string; message?: string; }

const ChatBubble: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const panelRef = useRef<HTMLDivElement>(null);

  // Open from external triggers (buttons in Hero / Services CTA)
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('openContactChat', handler);
    return () => window.removeEventListener('openContactChat', handler);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = t('contact.form.validation.required', 'Requis');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.validation.required', 'Requis');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.validation.invalidEmail', 'Courriel invalide');
    }
    if (!formData.subject.trim()) newErrors.subject = t('contact.form.validation.required', 'Requis');
    if (!formData.message.trim()) newErrors.message = t('contact.form.validation.required', 'Requis');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      if (process.env.NODE_ENV === 'development') {
        // PHP not available in dev — simulate success
        await new Promise(res => setTimeout(res, 600));
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setIsSubmitting(false);
        return;
      }
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: formData.name,
          courriel: formData.email,
          sujet: formData.subject,
          message: formData.message,
        }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Server error');
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('[ChatBubble] submit error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const field = (
    id: keyof FormData,
    label: string,
    type: 'text' | 'email' | 'textarea' = 'text'
  ) => (
    <div style={{ marginBottom: 10 }}>
      <label
        htmlFor={`chat-${id}`}
        style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 4 }}
      >
        {label} *
      </label>
      {type === 'textarea' ? (
        <textarea
          id={`chat-${id}`}
          name={id}
          rows={3}
          value={formData[id]}
          onChange={handleChange}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${errors[id] ? '#F87171' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 8,
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            padding: '8px 12px',
            outline: 'none',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = errors[id] ? '#F87171' : 'rgba(255,255,255,0.1)'; }}
        />
      ) : (
        <input
          id={`chat-${id}`}
          type={type}
          name={id}
          value={formData[id]}
          onChange={handleChange}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${errors[id] ? '#F87171' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 8,
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            padding: '8px 12px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = errors[id] ? '#F87171' : 'rgba(255,255,255,0.1)'; }}
        />
      )}
      {errors[id] && (
        <p style={{ color: '#FCA5A5', fontSize: '0.7rem', marginTop: 2, fontFamily: 'var(--font-body)' }}>
          {errors[id]}
        </p>
      )}
    </div>
  );

  return (
    <>
      {/* Chat panel */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          bottom: 88,
          right: 20,
          zIndex: 9999,
          width: 340,
          maxWidth: 'calc(100vw - 32px)',
          transform: isOpen ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(16px)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'transform 280ms cubic-bezier(0.34,1.36,0.64,1), opacity 220ms ease',
          transformOrigin: 'bottom right',
        }}
      >
        <div
          style={{
            borderRadius: 'var(--radius-2xl)',
            background: 'rgba(14,14,20,0.96)',
            border: '1px solid rgba(54,109,198,0.25)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(54,109,198,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1A3560 0%, #366DC6 100%)',
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 0 6px rgba(255,255,255,0.8)',
                }}
              />
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.9375rem', color: '#fff' }}>
                {t('contact.chat.title', 'Contactez-nous')}
              </span>
            </div>
            <button
              onClick={() => { setIsOpen(false); setSubmitStatus('idle'); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: '1.25rem', lineHeight: 1, padding: 4 }}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '16px 18px 18px', maxHeight: '65vh', overflowY: 'auto' }}>
            {submitStatus === 'success' ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
                <p style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1rem', color: 'var(--color-primary)' }}>
                  {t('contact.form.validation.success', 'Message envoyé !')}
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 6 }}>
                  {t('contact.chat.successHint', 'Nous vous répondrons rapidement.')}
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  style={{
                    marginTop: 16,
                    background: 'none',
                    border: '1px solid rgba(54,109,198,0.4)',
                    borderRadius: 8,
                    color: 'var(--color-primary)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    padding: '6px 16px',
                    cursor: 'pointer',
                  }}
                >
                  {t('contact.chat.newMessage', 'Nouveau message')}
                </button>
              </div>
            ) : (
              <>
                {submitStatus === 'error' && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: 'rgba(239,68,68,0.12)',
                      color: '#FCA5A5',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {t('contact.form.validation.error', 'Une erreur est survenue. Réessayez.')}
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  {field('name', t('contact.form.name', 'Nom'))}
                  {field('email', t('contact.form.email', 'Courriel'), 'email')}
                  {field('subject', t('contact.form.subject', 'Sujet'))}
                  {field('message', t('contact.form.message', 'Message'), 'textarea')}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: isSubmitting ? 'rgba(54,109,198,0.4)' : 'linear-gradient(135deg, #1A3560 0%, #366DC6 100%)',
                      border: 'none',
                      borderRadius: 10,
                      color: '#fff',
                      fontFamily: 'var(--font-display)',
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      marginTop: 4,
                      transition: 'opacity 200ms',
                    }}
                  >
                    {isSubmitting
                      ? t('contact.form.sending', 'Envoi…')
                      : t('contact.form.submit', 'Envoyer')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* FAB */}
      <button
        onClick={() => { setIsOpen(o => !o); if (submitStatus === 'success') setSubmitStatus('idle'); }}
        aria-label={isOpen ? 'Fermer le chat' : 'Ouvrir le chat contact'}
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1A3560 0%, #366DC6 100%)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(54,109,198,0.45)',
          transition: 'transform 220ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 200ms',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
      >
        {isOpen ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>
    </>
  );
};

export default ChatBubble;
